import { type QueryClient, queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import { graphql } from '@/gql'
import type { NooksQuery } from '@/gql/graphql'
import { gqlClient } from '@/lib/graphql'
import { subscribe } from '@/lib/wsClient'

/**
 * Every nook: the list in a lounge's sidebar, the one open on screen, the
 * mutations that reshape them, and the socket that keeps the list live.
 */

export const nookKeys = {
  all: ['nooks'] as const,
  /**
   * Every lounge's list, for a write that does not know which lounge.
   *
   * Distinct from `all` on purpose: `all` also matches `detail` entries, whose
   * envelope is `{ nook }` rather than `{ nooks }`, so a badge write addressed
   * at `all` would be handed a shape it cannot map over.
   */
  lists: () => [...nookKeys.all, 'list'] as const,
  list: (loungeId: string) => [...nookKeys.lists(), loungeId] as const,
  detail: (nookId: string) => [...nookKeys.all, 'detail', nookId] as const,
}

const NooksQueryDocument = graphql(`
  query Nooks($loungeId: UUID!) {
    nooks(loungeId: $loungeId) {
      id
      loungeId
      name
      topic
      position
      unreadCount
      mentionCount
      createdAt
      voiceParticipants {
        nookId
        joinedAt
        muted
        serverMuted
        cameraOn
        screenSharing
        user {
          id
          handle
          displayName
          avatarUrl
        }
      }
    }
  }
`)

const NookQueryDocument = graphql(`
  query Nook($id: UUID!) {
    nook(id: $id) {
      id
      loungeId
      name
      topic
      position
      unreadCount
      mentionCount
      createdAt
      voiceParticipants {
        nookId
        joinedAt
        muted
        serverMuted
        cameraOn
        screenSharing
        user {
          id
          handle
          displayName
          avatarUrl
        }
      }
    }
  }
`)

const CreateNookMutation = graphql(`
  mutation CreateNook($loungeId: UUID!, $name: String!) {
    createNook(loungeId: $loungeId, name: $name) {
      id
      loungeId
      name
      topic
      position
      unreadCount
      mentionCount
      createdAt
      voiceParticipants {
        nookId
        joinedAt
        muted
        serverMuted
        cameraOn
        screenSharing
        user {
          id
          handle
          displayName
          avatarUrl
        }
      }
    }
  }
`)

const RenameNookMutation = graphql(`
  mutation RenameNook($nookId: UUID!, $name: String!) {
    renameNook(nookId: $nookId, name: $name) {
      id
      name
    }
  }
`)

const SetNookTopicMutation = graphql(`
  mutation SetNookTopic($nookId: UUID!, $topic: String) {
    setNookTopic(nookId: $nookId, topic: $topic) {
      id
      topic
    }
  }
`)

const ReorderNooksMutation = graphql(`
  mutation ReorderNooks($loungeId: UUID!, $nookIds: [UUID!]!) {
    reorderNooks(loungeId: $loungeId, nookIds: $nookIds) {
      id
      position
    }
  }
`)

const DeleteNookMutation = graphql(`
  mutation DeleteNook($nookId: UUID!) {
    deleteNook(nookId: $nookId)
  }
`)

const LoungeStreamSubscription = graphql(`
  subscription LoungeStream($loungeId: UUID!) {
    loungeStream(loungeId: $loungeId) {
      kind
      nookId
      nook {
        id
        loungeId
        name
        topic
        position
        unreadCount
        mentionCount
        createdAt
        voiceParticipants {
          nookId
          joinedAt
          muted
          serverMuted
          cameraOn
          screenSharing
          user {
            id
            handle
            displayName
            avatarUrl
          }
        }
      }
    }
  }
`)

export const nooksQueryOptions = (loungeId: string) =>
  queryOptions({
    queryKey: nookKeys.list(loungeId),
    queryFn: () => gqlClient.request(NooksQueryDocument, { loungeId }),
    // The socket keeps this fresh, so a window refocus should not refetch it.
    staleTime: 30_000,
  })

export const nookQueryOptions = (nookId: string) =>
  queryOptions({
    queryKey: nookKeys.detail(nookId),
    queryFn: () => gqlClient.request(NookQueryDocument, { id: nookId }),
    staleTime: 30_000,
  })

export function useNooks(loungeId: string) {
  return useQuery(nooksQueryOptions(loungeId))
}

export function useNook(nookId: string) {
  return useQuery(nookQueryOptions(nookId))
}

export function useCreateNook(loungeId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (name: string) => gqlClient.request(CreateNookMutation, { loungeId, name }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: nookKeys.list(loungeId) })
    },
  })
}

export function useRenameNook() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: { nookId: string; name: string }) =>
      gqlClient.request(RenameNookMutation, input),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: nookKeys.all })
    },
  })
}

export function useSetNookTopic() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: { nookId: string; topic: string | null }) =>
      gqlClient.request(SetNookTopicMutation, input),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: nookKeys.all })
    },
  })
}

export function useReorderNooks(loungeId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (nookIds: string[]) =>
      gqlClient.request(ReorderNooksMutation, { loungeId, nookIds }),
    // Written straight into the cache before the round trip: dragging is a
    // direct manipulation, and a list that springs back to its old order for a
    // frame reads as the drag having failed.
    onMutate: async (nookIds) => {
      await client.cancelQueries({ queryKey: nookKeys.list(loungeId) })
      const previous = client.getQueryData<NooksQuery>(nookKeys.list(loungeId))

      if (previous) {
        const byId = new Map(previous.nooks.map((nook) => [nook.id, nook]))
        const reordered = nookIds
          .map((id, position) => {
            const nook = byId.get(id)
            return nook ? { ...nook, position } : undefined
          })
          .filter((nook) => nook !== undefined)

        client.setQueryData<NooksQuery>(nookKeys.list(loungeId), { nooks: reordered })
      }

      return { previous }
    },
    onError: (_error, _nookIds, context) => {
      if (context?.previous) {
        client.setQueryData(nookKeys.list(loungeId), context.previous)
      }
    },
    onSettled: async () => {
      await client.invalidateQueries({ queryKey: nookKeys.list(loungeId) })
    },
  })
}

export function useDeleteNook(loungeId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (nookId: string) => gqlClient.request(DeleteNookMutation, { nookId }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: nookKeys.list(loungeId) })
    },
  })
}

/**
 * Keep a lounge's nook list live for as long as the component is mounted.
 *
 * The socket is the only thing a component sees of realtime: it calls this and
 * renders `useNooks` as if nothing were moving.
 */
export function useLoungeStream(loungeId: string) {
  const client = useQueryClient()

  useEffect(() => {
    return subscribe(
      LoungeStreamSubscription,
      { loungeId },
      {
        onNext: ({ loungeStream: event }) => {
          applyLoungeEvent(client, loungeId, event)
        },
        // Any gap in the stream — a dropped socket, or a stream the server
        // ended because we fell behind — is repaired by asking again.
        onStart: () => {
          void client.invalidateQueries({ queryKey: nookKeys.list(loungeId) })
        },
      },
    )
  }, [client, loungeId])
}

type LoungeEvent = {
  kind: 'NOOK_CREATED' | 'NOOK_UPDATED' | 'NOOK_DELETED'
  nookId: string
  nook?: NooksQuery['nooks'][number] | null
}

/**
 * Fold one event into the cached list.
 *
 * Note the shape: the cached value is the whole GraphQL envelope (`{ nooks }`),
 * never the bare array — the same convention the rest of the app's cache
 * writes follow.
 */
function applyLoungeEvent(client: QueryClient, loungeId: string, event: LoungeEvent) {
  client.setQueryData<NooksQuery>(nookKeys.list(loungeId), (current) => {
    if (!current) return current

    if (event.kind === 'NOOK_DELETED') {
      return { nooks: current.nooks.filter((nook) => nook.id !== event.nookId) }
    }

    const incoming = event.nook
    if (!incoming) return current

    const known = current.nooks.some((nook) => nook.id === incoming.id)
    const nooks = known
      ? current.nooks.map((nook) =>
          nook.id === incoming.id
            ? {
                ...incoming,
                // Unread counts are per-viewer and the event is not: it carries
                // whatever the publisher saw, which is zero. Keeping ours is the
                // difference between a badge and a badge that clears itself when
                // somebody else renames the room.
                unreadCount: nook.unreadCount,
                mentionCount: nook.mentionCount,
                // Same trap, different field. A nook riding the bus carries no
                // presence — an event about a room being renamed says nothing
                // about who is sitting in it — so taking the event's empty list
                // would empty the voice row until the next refetch.
                voiceParticipants: nook.voiceParticipants,
              }
            : nook,
        )
      : [...current.nooks, incoming]

    return { nooks: [...nooks].sort((a, b) => a.position - b.position) }
  })
}
